import { normalizeCropCode } from "pages/admin/experiments/cropExtraction/cropCodes";
import { NamedDbfTable, findField, runDbfParser, toNumber, toString } from "pages/admin/experiments/cropExtraction/parsers/genericDbf";
import { ParserResult } from "pages/admin/experiments/cropExtraction/types";

// Several portals (Saarland, Schleswig-Holstein, parts of RLP) emit the same
// `Export_*_Shape/` package containing two shapefiles per export:
//   - `korrektur_*` — small correction layer, often empty
//   - `schlag_*`    — the actual field/crop layer
// Always prefer the latter; fall back to the largest non-empty table.
const pickSchlagTable = (tables: NamedDbfTable[]): NamedDbfTable | undefined => {
  const named = tables.find((t) => /^schlag(_|$)/.test(t.basename.toLowerCase()) && t.rows.length > 0);
  if (named) return named;
  const populated = tables.filter((t) => t.rows.length > 0);
  if (populated.length === 0) return undefined;
  return populated.reduce((max, t) => (t.rows.length > max.rows.length ? t : max), populated[0]);
};

export const parseSchleswigHolstein = (zipBuffer: ArrayBuffer): Promise<ParserResult> =>
  runDbfParser(zipBuffer, {
    notFoundMessage: "No populated DBF found in Export_*_Shape/",
    emptyMessage: "Schlag DBF table had no readable rows",
    pickTable: pickSchlagTable,
    buildExtractor: (table) => {
      // Schema observed in the DE-SL sample (and shared with neighbouring portals):
      //   NCODE / VNCODE   numeric crop code (current / previous year)
      //   CODE_BEZ         inline German crop description
      //   LAGE_BEZ         field name
      //   SCHLAGNR         field number, FLIK = field block ID
      //   GR / VJ_GR       area, already in hectares (decimals carried in DBF)
      const cropCodeNow = findField(table.rows, ["NCODE", "ncode"]);
      const cropCodePrev = findField(table.rows, ["VNCODE", "vncode"]);
      const inlineDescCol = findField(table.rows, ["CODE_BEZ", "code_bez"]);
      const fieldNameCol = findField(table.rows, ["LAGE_BEZ", "lage_bez", "bez", "schlagbez", "name"]);
      const fieldNumberCol = findField(table.rows, ["SCHLAGNR", "schlagnr"]);
      const flikCol = findField(table.rows, ["FLIK", "flik", "vflik"]);
      const areaNow = findField(table.rows, ["GR", "gr"]);
      const areaPrev = findField(table.rows, ["VJ_GR", "vj_gr"]);
      const areaHaCol = findField(table.rows, ["flaeche_ha", "flaeche", "groesse"]);

      return (row) => {
        const codeNow = cropCodeNow ? normalizeCropCode(toString(row[cropCodeNow])) : "";
        const codePrev = cropCodePrev ? normalizeCropCode(toString(row[cropCodePrev])) : "";
        const cropCode = codeNow || codePrev;

        const fieldNumber = fieldNumberCol ? toString(row[fieldNumberCol]) : "";
        const flik = flikCol ? toString(row[flikCol]) : "";
        const fieldName = fieldNameCol ? toString(row[fieldNameCol]) : "";

        const areaHa = (areaNow ? toNumber(row[areaNow]) : 0) || (areaPrev ? toNumber(row[areaPrev]) : 0) || (areaHaCol ? toNumber(row[areaHaCol]) : 0);

        return {
          fieldId: flik && fieldNumber ? `${flik}/${fieldNumber}` : flik || fieldNumber,
          fieldName: fieldName || fieldNumber || flik,
          cropCode,
          // CODE_BEZ is more authoritative than our generic dictionary (e.g.
          // "Mähweiden" vs the fallback "Mähweide"), so surface it inline.
          cropDescription: inlineDescCol ? toString(row[inlineDescCol]) : "",
          areaHa,
          fieldNumber: fieldNumber || undefined,
        };
      };
    },
  });
